import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Card, Modal } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';
import { fetchTodayHourData } from 'hooks/CallApi';
import { isDemoMode } from 'data/demoMockData';

const TodayUsageChart = ({ serviceType = 'sdk' }) => {
    const MAX_DISPLAY_SERVICES = 6;
    const PIE_COLORS = [
        '#2C7BE5', '#4A90E2', '#5AA9E6', '#7BBDF5', '#A3D4F7', '#CFE9FB',
        '#FF8C94', '#FFD166', '#06D6A0', '#118AB2', '#EF476F', '#FFC43D'
    ];

    const [selectedService, setSelectedService] = useState('all');
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showHourModal, setShowHourModal] = useState(false);
    const [selectedHourData, setSelectedHourData] = useState(null);
    const [showAllServices, setShowAllServices] = useState(false);

    // 내부 상태로 서비스와 시간별 데이터 관리
    const [services, setServices] = useState([]);
    const [hourlyData, setHourlyData] = useState({});

    const chartRef = useRef(null);

    // 현재 시간 ��져오기
    const currentHour = new Date().getHours();

    // 컴포넌트 상단에 zoneId 추출
    const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('zoneId:', zoneId);

    const generateDemoTodayData = () => {
        const svcNames = serviceType === 'sdk'
            ? ['예/적금', '입출금', '가입자 생성/조회', '대출']
            : serviceType === 'cf'
                ? ['Cloud Function 호출', '배포 관리']
                : ['API Gateway', 'API 요청 처리'];
        const today = new Date();
        const todayStr = today.toLocaleDateString('sv-SE', { timeZone: zoneId });
        const lastHour = today.getHours() - 1;
        const svcs = svcNames.map(name => {
            const hd = {};
            for (let h = 0; h <= Math.max(lastHour, 0); h++) {
                const hourStr = String(h).padStart(2, '0');
                const key = `${todayStr} ${hourStr}:00`;
                const base = h >= 9 && h <= 18 ? 60 : 15;
                hd[key] = {
                    avgAgentCount: Math.floor(Math.random() * base) + 5,
                    successCount: Math.floor(Math.random() * base) + 5,
                    errorCount: Math.floor(Math.random() * 3),
                    totalCount: Math.floor(Math.random() * base) + 8
                };
            }
            return { name, hourlyData: hd };
        });
        return { data: { services: svcs, namespaces: ['production', 'staging'] } };
    };

    const loadTodayHourlyData = async () => {
        setLoading(true);
        try {
            const response = isDemoMode()
                ? generateDemoTodayData()
                : await fetchTodayHourData(zoneId, serviceType);

            const data = response.data || {};

            if (data.services && Array.isArray(data.services)) {
                setServices(data.services);
            }

            const today = new Date();
            const todayStr = today.toLocaleDateString('sv-SE', { timeZone: zoneId });
            const lastHour = new Date().getHours() - 1;
            const hourlyDataObj = {};

            (data.services || []).forEach(service => {
                hourlyDataObj[service.name] = {};
                for (let hour = 0; hour <= lastHour; hour++) {
                    const hourStr = String(hour).padStart(2, '0');
                    const key = `${todayStr} ${hourStr}:00`;
                    hourlyDataObj[service.name][hour] = service.hourlyData?.[key]?.avgAgentCount ?? 0;
                }
            });

            setHourlyData(hourlyDataObj);
            console.log("오늘 시간별 데이터 로드 완료:", new Date().toLocaleTimeString());
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            console.error('오늘 사용량 데이터 로드 실패:', err);
            setError('데이터 로드 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setServices([]);
        setHourlyData({});
        setSelectedService('all');

        loadTodayHourlyData();

        const setupHourlyUpdate = () => {
            const now = new Date();
            const nextHour = new Date(now);
            nextHour.setHours(now.getHours() + 1, 0, 0, 0);
            const timeUntilNextHour = nextHour - now;

            const timerId = setTimeout(() => {
                loadTodayHourlyData();
                const hourlyInterval = setInterval(loadTodayHourlyData, 60 * 60 * 1000);
                return () => clearInterval(hourlyInterval);
            }, timeUntilNextHour);

            return () => clearTimeout(timerId);
        };

        const clearTimer = setupHourlyUpdate();
        return clearTimer;
    }, [serviceType]);

    const modalUsageDetails = useMemo(() => {
        if (!selectedHourData?.usageDetails) return [];
        return showAllServices
            ? selectedHourData.usageDetails
            : selectedHourData.usageDetails.slice(0, MAX_DISPLAY_SERVICES);
    }, [selectedHourData, showAllServices]);

    // 차트에 표시할 서비스 목록
    const displayedServices = useMemo(() => {
        return showAllServices ? services : services.slice(0, MAX_DISPLAY_SERVICES);
    }, [services, showAllServices]);

    // 시간별 데이터 집계 로직
    const aggregatedData = useMemo(() => {
        // 시간별 집계를 위한 배열 초기화 (0시부터 현재 시간까지)
        const now = new Date();
        const lastHour = now.getHours() - 1; // 16시면 15

        const hoursList = Array.from({ length: lastHour + 1 }, (_, i) => i);
        const hoursData = Array(lastHour + 1).fill(0);

        if (Object.keys(hourlyData).length === 0) {
            return { hoursList, hoursData };
        }

        if (selectedService === 'all') {
            // 모든 서비스 집계
            services.forEach(service => {
                const serviceData = hourlyData[service.name] || {};

                hoursList.forEach((hour, index) => {
                    // 해당 시간의 데이터가 있으면 합산
                    hoursData[index] += (serviceData[hour] || 0);
                });
            });
        } else {
            // 특정 서비스만 표시
            const serviceData = hourlyData[selectedService] || {};

            hoursList.forEach((hour, index) => {
                hoursData[index] = serviceData[hour] || 0;
            });
        }

        return { hoursList, hoursData };
    }, [hourlyData, selectedService, services, currentHour]);

    // 데이터가 비어있는지 확인
    const isDataEmpty = useMemo(() => {
        return aggregatedData.hoursData.every(value => value === 0);
    }, [aggregatedData]);

    const getServiceColor = (index) => {
        const colors = [
            '#2C7BE5', // 진한 블루
            '#4A90E2', // 중간 블루
            '#5AA9E6', // 연한 블루
            '#7BBDF5', // 더 연한 블루
            '#A3D4F7', // 옅은 블루
            '#CFE9FB'  // 매우 옅은 블루
        ];
        return colors[index % colors.length];
    };
    // 차트 옵션 계산
    const chartOptions = useMemo(() => {
        // 0시부터 현재 시간까지의 레이블 생성
        const hours = aggregatedData.hoursList.map(hour => `${hour}시`);

        // 누적 영역 그래프: 모든 서비스 선택 시
        let series;
        if (selectedService === 'all') {
            series = displayedServices.map((service, idx) => ({
                name: service.name,
                type: 'line',
                stack: 'total',
                data: aggregatedData.hoursList.map(hour => (hourlyData[service.name]?.[hour] || 0)),
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: getServiceColor(idx) + '33' }, // 상단: 연한 색 (투명도 20%)
                            { offset: 1, color: getServiceColor(idx) + '05' }  // 하단: 더 연한 색 (투명도 2%)
                        ]
                    }
                },
                symbol: 'circle',
                symbolSize: 4,  // 기본 크기를 더 작게
                emphasis: {
                    itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 3,
                        shadowBlur: 12,
                        shadowColor: 'rgba(0, 0, 0, 0.4)'
                    },
                    symbolSize: 20  // hover 시 더 크게 (4px → 20px, 5배 확대!)
                },
                select: {
                    itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 3
                    }
                },
                // 더 큰 클릭 영역을 위한 설정
                symbolKeepAspect: true,
                showSymbol: true,
                hoverAnimation: true,
                lineStyle: {
                    width: 3
                },
                itemStyle: {
                    color: getServiceColor(idx)
                },
                smooth: false
            }));
        } else {
            // 단일 서비스 라인
            series = [{
                name: selectedService,
                type: 'line',
                data: aggregatedData.hoursData,
                smooth: false,
                symbol: 'circle',
                symbolSize: 4,  // 기본 크기를 더 작게
                emphasis: {
                    itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 4,
                        shadowBlur: 15,
                        shadowColor: 'rgba(100, 169, 243, 0.6)'
                    },
                    symbolSize: 22  // hover 시 더 크게 (4px → 22px, 5.5배 확대!)
                },
                select: {
                    itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 4
                    }
                },
                // 더 큰 클릭 영역을 위한 설정
                symbolKeepAspect: true,
                showSymbol: true,
                hoverAnimation: true,
                lineStyle: {
                    color: '#64A9F3',
                    width: 3
                },
                itemStyle: {
                    color: '#64A9F3'
                },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [{
                            offset: 0, color: 'rgba(100, 169, 243, 0.5)'
                        }, {
                            offset: 1, color: 'rgba(100, 169, 243, 0.05)'
                        }]
                    }
                }
            }];
        }

        const options = {
            tooltip: {
                trigger: 'axis',
                triggerOn: 'mousemove',
                enterable: false,
                hideDelay: 50,
                showDelay: 30,
                confine: true, // 툴팁을 차트 영역 내에 제한
                axisPointer: {
                    type: 'cross',
                    snap: true,
                    animation: true,
                    animationDuration: 200,
                    lineStyle: {
                        color: 'var(--falcon-primary)',
                        width: 1,
                        type: 'solid',
                        opacity: 0.8
                    },
                    crossStyle: {
                        color: 'var(--falcon-primary)',
                        width: 1,
                        opacity: 0.6
                    },
                    label: { show: false }
                },
                backgroundColor: 'var(--falcon-quaternary-bg)',
                borderColor: 'var(--falcon-border-color)',
                borderWidth: 1,
                borderRadius: 8,
                padding: [12, 16],
                textStyle: {
                    color: 'var(--falcon-body-color)',
                    fontSize: 13
                },
                extraCssText: 'box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);',
                formatter: (params) => {
                    if (selectedService === 'all') {
                        // 누적 영역: 서비스별 값 모두 표시
                        const title = `<div style="font-weight: 600; margin-bottom: 8px; color: var(--falcon-primary);">${params[0].name}</div>`;
                        const content = params.map(p =>
                            `<div style="margin: 2px 0;">${p.marker}<span style="font-weight: 500;">${p.seriesName}:</span> <span style="font-weight: 600;">${p.value.toLocaleString()} 건</span></div>`
                        ).join('');
                        return title + content;
                    } else {
                        const hour = params[0].name;
                        const value = params[0].value;
                        return `<div style="font-weight: 600; margin-bottom: 6px; color: var(--falcon-primary);">${hour}</div>
                                <div style="display: flex; align-items: center; gap: 8px;">
                                    ${params[0].marker}
                                    <span style="font-weight: 500;">${selectedService}:</span> 
                                    <span style="font-weight: 600; color: var(--falcon-success);">${value.toLocaleString()} 건</span>
                                </div>`;
                    }
                }
            },
            legend: selectedService === 'all'
                ? (displayedServices.length > MAX_DISPLAY_SERVICES
                    ? {
                        data: displayedServices.map(s => s.name),
                        bottom: 0,
                        left: 0,
                        right: 0,
                        orient: 'horizontal',
                        type: 'scroll',
                        textStyle: { color: '#333' },
                        itemWidth: 14,
                        itemHeight: 14,
                        padding: [8, 10, 8, 10],
                        pageIconColor: '#888',           // 활성화 버튼: 하얀색
                        pageIconInactiveColor: '#ccc', // 비활성: 연한 하양
                        pageTextStyle: { color: '#888' } // 페이지 번호: 하얀색
                    }
                    : {
                        data: displayedServices.map(s => s.name),
                        top: 10,
                        left: 'center',
                        orient: 'horizontal',
                        type: 'plain',
                        textStyle: { color: '#333' },
                        itemWidth: 14,
                        itemHeight: 14,
                        padding: [8, 10, 8, 10]
                    }
                )
                : undefined,
            grid: {
                left: displayedServices.length > MAX_DISPLAY_SERVICES ? -60 : 80,
                right: '5%',
                bottom: displayedServices.length > MAX_DISPLAY_SERVICES ? 30 : '10%',
                top: displayedServices.length > MAX_DISPLAY_SERVICES ? 40 : 40,
                containLabel: false
            },
            // 전체 차트 영역에서 마우스 이벤트 감지 및 최적화
            silent: false,
            animation: true,
            animationDuration: 300,
            animationEasing: 'cubicOut',
            xAxis: {
                type: 'category',
                data: hours,
                boundaryGap: false,
                axisLabel: {
                    interval: hours.length > 12 ? 2 : 1,
                    color: '#888',
                    fontSize: 10,
                    formatter: (value) => value
                },
                axisLine: {
                    lineStyle: { color: '#ccc' }
                },
                axisTick: {
                    show: false
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#eee',
                        type: 'dashed',
                        width: 1
                    }
                },
                //x축 라벨만 다시 켜기
                axisPointer: {
                    label: {
                        show: true,
                        backgroundColor: 'var(--falcon-primary)',
                        borderRadius: 6,
                        padding: [4, 8],
                        color: '#fff',
                        fontSize: 12
                    }
                }
            },
            yAxis: {
                type: 'value',
                show: false,
                axisLabel: {
                    show: false
                },
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                },
                max: function (value) {
                    return value.max === 0 ? 10 : value.max;
                },
                splitLine: {
                    show: false
                },
                // Y축 라벨은 확실히 끄기
                axisPointer: { label: { show: false } }
            },
            series
        };

        // 데이터가 없는 경우 메시지 표시
        if (isDataEmpty) {
            return {
                ...options,
                title: {
                    text: loading ? '데이터 로딩 중...' : '데이터 없음',
                    left: 'center',
                    top: 'middle',
                    textStyle: {
                        color: '#888',
                        fontSize: 16,
                        fontWeight: 'normal'
                    }
                }
            };
        }

        return options;
    }, [aggregatedData, selectedService, isDataEmpty, loading, displayedServices, hourlyData]);

    // 서비스 선택 핸들러
    const handleServiceChange = (e) => {
        setSelectedService(e.target.value);
    };

    // 시간대 클릭 핸들러
    const handleHourClick = (params) => {
        if (params.dataIndex <= currentHour) {
            const hourIndex = params.dataIndex;
            const hourValue = aggregatedData.hoursList[hourIndex];
            const usageDetails = [];

            if (selectedService === 'all') {
                // 모든 서비스에 대한 시간별 데이터 수집 (현재 코드 유지)
                services.forEach((service, index) => {
                    const serviceHourlyData = hourlyData[service.name] || {};
                    const usage = serviceHourlyData[hourValue] || 0;

                    if (usage > 0) {
                        usageDetails.push({
                            name: service.name,
                            usage: usage,
                            color: getServiceColor(index)
                        });
                    }
                });

                // 모달 데이터 설정 및 표시 (파이 차트용)
                setSelectedHourData({
                    hour: `${hourValue}시`,
                    totalUsage: params.value,
                    usageDetails: usageDetails,
                    chartType: 'pie' // 파이 차트 타입 지정
                });
            } else {
                // 개별 서비스: 해당 시간의 상세 데이터 추출
                const serviceObj = services.find(s => s.name === selectedService);
                const today = new Date();
                const todayStr = today.toLocaleDateString('sv-SE', { timeZone: zoneId });
                const hourStr = String(hourValue).padStart(2, '0');
                const key = `${todayStr} ${hourStr}:00`;
                const detail = serviceObj?.hourlyData?.[key] || {};

                // 파이차트용 데이터 구성
                const usageDetails = [
                    {
                        name: '성공',
                        usage: detail.successCount ?? 0,
                        color: '#6EC6FF' // 파스텔 스카이블루 (선명)
                    },
                    {
                        name: '실패',
                        usage: detail.errorCount ?? 0,
                        color: '#FF8C94' // 파스텔 코랄 (선명)
                    }
                ];

                setSelectedHourData({
                    hour: `${hourValue}시`,
                    totalUsage: detail.totalCount ?? 0,
                    usageDetails,
                    chartType: 'pie'
                });
            }

            setShowHourModal(true);
        }
    };

    function getPieChartData(details, max = 6) {
        if (!details) return [];
        if (details.length <= max) return details.map((d, i) => ({
            ...d,
            color: PIE_COLORS[i % PIE_COLORS.length]
        }));

        // 상위 max개 + 기타
        const sorted = [...details].sort((a, b) => b.usage - a.usage);
        const top = sorted.slice(0, max).map((d, i) => ({
            ...d,
            color: PIE_COLORS[i % PIE_COLORS.length]
        }));
        const etcUsage = sorted.slice(max).reduce((sum, d) => sum + d.usage, 0);
        if (etcUsage > 0) {
            top.push({
                name: 'others',
                usage: etcUsage,
                color: '#BDBDBD'
            });
        }
        return top;
    }

    // 파이 차트 옵션 생성
    const generateHourPieChartOptions = (usageDetails) => {
        const pieData = getPieChartData(usageDetails, MAX_DISPLAY_SERVICES);
        return {
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)'
            },
            legend: {
                orient: 'horizontal',
                bottom: 0,
                data: pieData.map((detail) => detail.name),
                textStyle: { color: '#333', fontSize: 12 },
                itemWidth: 14,
                itemHeight: 14,
                type: 'scroll',
                pageIconColor: '#64A9F3',           // 활성화 버튼: 연한 하늘색
                pageIconInactiveColor: '#A3D4F7',   // 비활성: 더 연한 하늘색
                pageTextStyle: { color: '#64A9F3' } // 페이지 번호: 연한 하늘색
            },
            series: [
                {
                    type: 'pie',
                    radius: ['50%', '75%'],
                    center: ['50%', '45%'],
                    data: pieData.map((detail) => ({
                        value: detail.usage,
                        name: detail.name,
                        itemStyle: { color: detail.color }
                    })),
                    label: {
                        show: selectedService !== 'all', // 개별 서비스일 때만 가운데 라벨 표시
                        position: 'center',
                        formatter: () =>
                            selectedHourData?.totalUsage
                                ? `{a|${selectedHourData.totalUsage.toLocaleString()}}\n{b|전체 건수}`
                                : '',
                        rich: {
                            a: {
                                fontSize: 20,
                                fontWeight: 'bold',
                                color: '#333'
                            },
                            b: {
                                fontSize: 13,
                                color: '#888',
                                padding: [4, 0, 0, 0]
                            }
                        }
                    },
                    labelLine: {
                        show: false
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '14',
                            fontWeight: 'bold'
                        }
                    }
                }
            ]
        };
    };

    // 막대 그래프 옵션 생성 함수 추가
    const generateHourBarChartOptions = (timeSeriesData, selectedHour) => {
        const hours = timeSeriesData.map(item => item.hour);
        const values = timeSeriesData.map(item => item.usage);

        return {
            tooltip: {
                trigger: 'axis',
                formatter: '{b}: {c} 건'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: hours,
                axisLabel: {
                    interval: hours.length > 12 ? 1 : 0,
                    rotate: 45
                }
            },
            yAxis: {
                type: 'value'
            },
            series: [{
                data: values.map((value, index) => ({
                    value: value,
                    itemStyle: {
                        color: index === selectedHour ? '#2C7BE5' : '#A3D4F7'
                    }
                })),
                type: 'bar',
                showBackground: true,
                backgroundStyle: {
                    color: 'rgba(180, 180, 180, 0.2)'
                }
            }]
        };
    };

    const generateStackedBarChartOptions = (hourlyDetailData) => {
        // hourlyDetailData: { 'YYYY-MM-DD HH:00': { errorCount, successCount, totalCount } }
        const hourKeys = Object.keys(hourlyDetailData).sort(); // 문자열 정렬(ISO 포맷이면 시간순 정렬됨)
        const errorData = [];
        const successData = [];
        const totalData = [];

        hourKeys.forEach(hourKey => {
            const d = hourlyDetailData[hourKey] || {};
            errorData.push(d.errorCount ?? 0);
            successData.push(d.successCount ?? 0);
            totalData.push(d.totalCount ?? 0);
        });

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            legend: {
                data: ['성공', '실패', '전체'],
                top: 10
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: hourKeys.map(h => h.slice(11, 16)) // 'HH:00'
            },
            yAxis: {
                type: 'value'
            },
            series: [
                {
                    name: '성공',
                    type: 'bar',
                    stack: 'total',
                    itemStyle: { color: '#4A90E2' },
                    data: successData
                },
                {
                    name: '실패',
                    type: 'bar',
                    stack: 'total',
                    itemStyle: { color: '#E24A4A' },
                    data: errorData
                },
                {
                    name: '전체',
                    type: 'bar',
                    stack: false,
                    itemStyle: { color: '#A3D4F7' },
                    data: totalData,
                    barGap: '-100%',
                    barWidth: 10,
                    z: 0
                }
            ]
        };
    };

    // 리사이즈 처리
    useEffect(() => {
        const handleResize = () => {
            if (chartRef.current) {
                const chart = chartRef.current.getEchartsInstance();
                chart.resize();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <Card className="bg-line-chart-gradient">
                <Card.Header className="bg-transparent py-3">
                    <div className="row align-items-center g-0">
                        <div className="col">
                            <h4 className="text-dark mb-0">오늘 사용량</h4>
                        </div>
                        <div className="col-auto d-flex align-items-center">
                            <select
                                className="form-select form-select-sm"
                                id="dashboard-chart-select"
                                value={selectedService}
                                onChange={handleServiceChange}
                                disabled={loading}
                            >
                                <option value="all">모든 서비스</option>
                                {services.map((service, index) => (
                                    <option key={index} value={service.name}>
                                        {service.name}
                                    </option>
                                ))}
                            </select>
                            {/* 더보기 버튼 */}
                            {selectedService === 'all' && services.length > MAX_DISPLAY_SERVICES && (
                                <button
                                    className="btn btn-link btn-sm text-dark ms-2"
                                    style={{ whiteSpace: 'nowrap', minWidth: 80 }}
                                    onClick={() => setShowAllServices(v => !v)}
                                >
                                    {showAllServices ? '간략히 보기' : '더보기'}
                                </button>
                            )}
                        </div>
                    </div>
                </Card.Header>
                <Card.Body style={{ height: '300px', padding: 0 }}>
                    <div style={{ height: '100%', width: '100%' }}>
                        <ReactECharts
                            ref={chartRef}
                            option={chartOptions}
                            style={{ height: '100%', width: '100%' }}
                            onEvents={{
                                click: handleHourClick
                            }}
                        />
                    </div>
                    {error && (
                        <div className="position-absolute top-50 start-50 translate-middle">
                            <div className="badge bg-danger">
                                {error}
                            </div>
                        </div>
                    )}
                </Card.Body>
                <Card.Footer className="bg-transparent text-dark text-end">
                    <small className="opacity-75">
                        * {currentHour}시 기준 데이터입니다. 매 정각에 자동 업데이트됩니다.
                    </small>
                </Card.Footer>
            </Card>

            {/* 시간별 상세 모달 */}
            <Modal
                show={showHourModal}
                onHide={() => setShowHourModal(false)}
                centered
                size="md"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedService === 'all'
                            ? `${selectedHourData?.hour} 사용량 정보`
                            : `${selectedService} ${selectedHourData?.hour} 사용량 정보`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        {selectedHourData?.chartType === 'pie' ? (
                            <ReactECharts
                                option={generateHourPieChartOptions(selectedHourData?.usageDetails || [])}
                                style={{ height: '300px', width: '100%' }}
                            />
                        ) : (
                            <ReactECharts
                                option={generateStackedBarChartOptions(selectedHourData?.hourlyDetailData || {})}
                                style={{ height: '300px', width: '100%' }}
                            />
                        )}
                    </div>
                    {/* 전체/간략 보기 토글 버튼 */}
                    {selectedHourData?.usageDetails?.length > MAX_DISPLAY_SERVICES && (
                        <div className="mb-2 text-end">
                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setShowAllServices(v => !v)}
                            >
                                {showAllServices ? '간략히 보기' : '전체 서비스 보기'}
                            </button>
                        </div>
                    )}
                    {selectedHourData?.chartType === 'pie' && (
                        <>
                            {/* 표 스크롤 영역 추가 */}
                            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                <ul className="list-group">
                                    {modalUsageDetails?.map((detail, index) => (
                                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="rounded-circle me-2"
                                                    style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        backgroundColor: detail.color
                                                    }}
                                                ></div>
                                                {detail.name}
                                            </div>
                                            <span>{detail.usage.toLocaleString()} 사용량</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}

                    {selectedHourData?.chartType === 'bar' && (
                        <div className="text-center p-3">
                            <h5>{selectedService}의 오늘 시간별 사용량 추이</h5>
                            <p className="text-muted">* {selectedHourData?.hour}에 {selectedHourData?.totalUsage.toLocaleString()} 건의 사용량이 발생했습니다.</p>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default TodayUsageChart;